import React, { useEffect, useRef, useState } from 'react';
import { DEFAULT_TICKET_FOOTER } from '../../utils/ticketFooter';
import { Link, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import LogoCropInput from '../../components/LogoCropInput';
import { useAuth } from '../../context/AuthContext';
import { changeCompanyPassword, updateCompanyProfile } from '../../api/companyProfile';
import './CompanyProfilePage.css';

// Le backend renvoie les erreurs DRF en JSON (ex: {"old_password": ["..."]})
// dans err.message — on essaie de recuperer un message lisible, sinon fallback generique.
const firstErrorMessage = (err, fallback) => {
  try {
    const detail = JSON.parse(err.message);
    const firstKey = Object.keys(detail)[0];
    const val = detail[firstKey];
    return Array.isArray(val) ? val[0] : String(val);
  } catch {
    return fallback;
  }
};

const CompanyProfilePage = () => {
  const { company, setCompany } = useAuth();
  const location = useLocation();
  const passwordCardRef = useRef(null);

  // Arrive avec #password (lien "Mot de passe" du popover AccountMenu.jsx) ->
  // scroll direct sur la 2e carte, sans dupliquer ce formulaire ailleurs.
  useEffect(() => {
    if (location.hash === '#password' && passwordCardRef.current) {
      passwordCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);
  const [address, setAddress] = useState(company?.address || '');
  const [phone, setPhone] = useState(company?.phone || '');
  const [ticketFooter, setTicketFooter] = useState(company?.ticket_footer || DEFAULT_TICKET_FOOTER);

  // Les infos de la company peuvent arriver APRES le premier rendu : on
  // (re)remplit le formulaire quand la company chargee change (pas a chaque
  // sauvegarde : meme id -> pas d'ecrasement de la saisie en cours).
  useEffect(() => {
    if (!company) return;
    setAddress(company.address || '');
    setPhone(company.phone || '');
    setTicketFooter(company.ticket_footer || DEFAULT_TICKET_FOOTER);
  }, [company?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSaved, setPwdSaved] = useState(false);

  // Delai d'annulation POS (Company.nullify_window_minutes) : vide = pas de delai.
  const [nullifyWindow, setNullifyWindow] = useState(company?.nullify_window_minutes ?? '');
  const [nwSaving, setNwSaving] = useState(false);
  const [nwError, setNwError] = useState('');
  const [nwSaved, setNwSaved] = useState(false);
  useEffect(() => {
    if (company) setNullifyWindow(company.nullify_window_minutes ?? '');
  }, [company?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [commissionVisibility, setCommissionVisibility] = useState(company?.commission_visibility ?? 'disabled');
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [commissionError, setCommissionError] = useState('');
  const [commissionSaved, setCommissionSaved] = useState(false);
  const [epargneVisibility, setEpargneVisibility] = useState(company?.epargne_visibility ?? 'disabled');
  const [epargneSaving, setEpargneSaving] = useState(false);
  const [epargneError, setEpargneError] = useState('');
  const [epargneSaved, setEpargneSaved] = useState(false);

  useEffect(() => {
    if (!company) return;
    setCommissionVisibility(company.commission_visibility ?? 'disabled');
    setEpargneVisibility(company.epargne_visibility ?? 'disabled');
  }, [company?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCommissionVisibilitySubmit = async (e) => {
    e.preventDefault();
    setCommissionSaving(true);
    setCommissionError('');
    setCommissionSaved(false);
    try {
      const formData = new FormData();
      formData.append('commission_visibility', commissionVisibility);
      const updated = await updateCompanyProfile(formData);
      setCompany(updated);
      setCommissionVisibility(updated.commission_visibility ?? 'disabled');
      setCommissionSaved(true);
    } catch (err) {
      setCommissionError(firstErrorMessage(err, 'Sauvegarde impossible.'));
    } finally {
      setCommissionSaving(false);
    }
  };

  const handleEpargneVisibilitySubmit = async (e) => {
    e.preventDefault();
    setEpargneSaving(true);
    setEpargneError('');
    setEpargneSaved(false);
    try {
      const formData = new FormData();
      formData.append('epargne_visibility', epargneVisibility);
      const updated = await updateCompanyProfile(formData);
      setCompany(updated);
      setEpargneVisibility(updated.epargne_visibility ?? 'disabled');
      setEpargneSaved(true);
    } catch (err) {
      setEpargneError(firstErrorMessage(err, 'Sauvegarde impossible.'));
    } finally {
      setEpargneSaving(false);
    }
  };

  const handleNullifyWindowSubmit = async (e) => {
    e.preventDefault();
    setNwSaving(true);
    setNwError('');
    setNwSaved(false);
    try {
      const formData = new FormData();
      formData.append('nullify_window_minutes', String(nullifyWindow).trim());
      const updated = await updateCompanyProfile(formData);
      setCompany(updated);
      setNullifyWindow(updated.nullify_window_minutes ?? '');
      setNwSaved(true);
    } catch (err) {
      setNwError(firstErrorMessage(err, 'Sauvegarde impossible.'));
    } finally {
      setNwSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const formData = new FormData();
      formData.append('address', address);
      formData.append('phone', phone);
      formData.append('ticket_footer', ticketFooter);
      if (logoFile) formData.append('logo', logoFile, 'logo.png');

      const updated = await updateCompanyProfile(formData);
      setCompany(updated);
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdSaving(true);
    setPwdError('');
    setPwdSaved(false);
    if (newPassword !== confirmPassword) {
      setPwdError('Les deux mots de passe ne correspondent pas.');
      setPwdSaving(false);
      return;
    }
    try {
      await changeCompanyPassword(oldPassword, newPassword);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwdSaved(true);
    } catch (err) {
      setPwdError(firstErrorMessage(err, 'Changement impossible.'));
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <Layout>
      <p className="cp-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <span className="bc-current">Parametres</span>
        {' >> '}
        <span className="bc-current">Mon Compte</span>
      </p>

      <div className="cp-card">
        <div className="cp-card-header">
          <span className="cp-card-title">Mon Compte</span>
        </div>
        <form className="cp-card-body" onSubmit={handleSubmit}>
          <div className="cp-row">
            <label className="cp-label">Nom</label>
            <span className="cp-value">{company?.name}</span>
          </div>

          <div className="cp-row">
            <label className="cp-label">Username</label>
            <span className="cp-value">{company?.username}</span>
          </div>

          <div className="cp-row">
            <label className="cp-label">Adresse</label>
            <input
              className="cp-input"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="cp-row">
            <label className="cp-label">Telephone</label>
            <input
              className="cp-input"
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="cp-row cp-row-top">
            <label className="cp-label">Description ticket</label>
            <textarea
              className="cp-textarea"
              value={ticketFooter}
              onChange={(e) => setTicketFooter(e.target.value)}
              placeholder="Texte imprimé en bas du ticket, sous les paris (mention au porteur...)"
            />
          </div>
          <p className="cp-hint">
            Texte imprimé en bas de chaque ticket. Le texte proposé est celui du POS par
            défaut : modifie-le puis Sauver pour personnaliser (vide = texte par défaut).
          </p>
          <button
            type="button"
            className="cp-link-btn"
            onClick={() => setTicketFooter(DEFAULT_TICKET_FOOTER)}
          >
            Remettre le texte par défaut
          </button>

          <div className="cp-row cp-row-top">
            <label className="cp-label">Logo</label>
            <LogoCropInput currentUrl={company?.logo} onChange={setLogoFile} />
          </div>

          {error && <p className="cp-error">{error}</p>}
          {saved && <p className="cp-saved">Sauvegarde.</p>}

          <button type="submit" className="cp-save-btn" disabled={saving}>
            {saving ? 'Sauvegarde...' : 'Sauver'}
          </button>
        </form>
      </div>

      <div className="cp-card">
        <div className="cp-card-header">
          <span className="cp-card-title">Commission</span>
        </div>
        <form className="cp-card-body" onSubmit={handleCommissionVisibilitySubmit}>
          <div className="cp-row">
            <label className="cp-label" htmlFor="commission-visibility">Afficher la commission</label>
            <select id="commission-visibility" className="cp-input" value={commissionVisibility} onChange={(e) => setCommissionVisibility(e.target.value)}>
              <option value="pos">POS seulement</option>
              <option value="web">Web</option>
              <option value="both">POS et Web</option>
              <option value="disabled">Désactivé</option>
            </select>
          </div>
          {commissionError && <p className="cp-error">{commissionError}</p>}
          {commissionSaved && <p className="cp-saved">Sauvegarde.</p>}
          <button type="submit" className="cp-save-btn" disabled={commissionSaving}>
            {commissionSaving ? 'Sauvegarde...' : 'Sauver'}
          </button>
        </form>
      </div>

      <div className="cp-card">
        <div className="cp-card-header">
          <span className="cp-card-title">Épargne</span>
        </div>
        <form className="cp-card-body" onSubmit={handleEpargneVisibilitySubmit}>
          <div className="cp-row">
            <label className="cp-label" htmlFor="epargne-visibility">Afficher l&apos;épargne</label>
            <select id="epargne-visibility" className="cp-input" value={epargneVisibility} onChange={(e) => setEpargneVisibility(e.target.value)}>
              <option value="pos">POS seulement</option>
              <option value="web">Web</option>
              <option value="both">POS et Web</option>
              <option value="disabled">Désactivé</option>
            </select>
          </div>
          {epargneError && <p className="cp-error">{epargneError}</p>}
          {epargneSaved && <p className="cp-saved">Sauvegarde.</p>}
          <button type="submit" className="cp-save-btn" disabled={epargneSaving}>
            {epargneSaving ? 'Sauvegarde...' : 'Sauver'}
          </button>
        </form>
      </div>

<div className="cp-card">
  <div className="cp-card-header">
    <span className="cp-card-title">Annulation de fiches (POS)</span>
  </div>
          <form className="cp-card-body" onSubmit={handleNullifyWindowSubmit}>
          <div className="cp-row">
            <label className="cp-label">Delai (minutes)</label>
            <input
              className="cp-input"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={nullifyWindow}
              onChange={(e) => setNullifyWindow(e.target.value)}
              placeholder="Pas de delai"
            />
          </div>
          <p className="cp-hint">
            Temps apres la creation d&apos;une fiche pendant lequel une banque autorisee a
            annuler peut encore l&apos;annuler depuis le POS. Ex. 10 : une fiche faite a
            12:34 ne peut plus etre annulee par la banque apres 12:44 — seule la company
            peut encore le faire. Vide = pas de delai (annulation possible le jour meme).
          </p>

          {nwError && <p className="cp-error">{nwError}</p>}
          {nwSaved && <p className="cp-saved">Sauvegarde.</p>}

          <button type="submit" className="cp-save-btn" disabled={nwSaving}>
            {nwSaving ? 'Sauvegarde...' : 'Sauver'}
          </button>
        </form>
      </div>

      <div className="cp-card" ref={passwordCardRef}>
        <div className="cp-card-header">
          <span className="cp-card-title">Changer le mot de passe</span>
        </div>
        <form className="cp-card-body" onSubmit={handlePasswordSubmit}>
          <div className="cp-row">
            <label className="cp-label">Mot de passe actuel</label>
            <input
              className="cp-input"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
            />
          </div>

          <div className="cp-row">
            <label className="cp-label">Nouveau mot de passe</label>
            <input
              className="cp-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={6}
              required
            />
          </div>

          <div className="cp-row">
            <label className="cp-label">Confirmer</label>
            <input
              className="cp-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={6}
              required
            />
          </div>

          {pwdError && <p className="cp-error">{pwdError}</p>}
          {pwdSaved && <p className="cp-saved">Mot de passe changé.</p>}

          <button type="submit" className="cp-save-btn" disabled={pwdSaving}>
            {pwdSaving ? 'Sauvegarde...' : 'Changer'}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default CompanyProfilePage;
